import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { readGiftConfig, resolveGiftUpdates, countMainUnits } from '@theme/gift-rules';

const SOURCE_ID = 'gift-manager-component';

/**
 * Keeps the free gifts in the cart in step with how much of the main product
 * is in it. The rules themselves live in gift-rules.js; this component owns the
 * network calls and telling the rest of the theme that the cart moved.
 *
 * It reconciles on every cart update rather than only when the cart drawer is
 * built. Adding to cart from a product page used to reach nothing at all, so a
 * qualifying order arrived without its gift unless the customer then touched a
 * quantity in the drawer.
 */
class GiftManagerComponent extends Component {
  /** @type {boolean} */
  #isProcessing = false;

  /** @returns {import('./gift-rules').GiftConfig | null} */
  get #config() {
    return readGiftConfig(this);
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.#config) return;

    document.documentElement.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // An empty cart can never owe anyone a gift, so skip the request entirely.
    if (parseInt(this.dataset.cartItemCount || '0', 10) > 0) {
      this.#reconcileFromServer();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.documentElement.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {any} event */
  #onCartUpdate = (event) => {
    // Our own writes already left the cart correct.
    if (event?.detail?.sourceId === SOURCE_ID) return;
    if (this.#isProcessing || !this.#config) return;

    const cart = event?.detail?.resource;

    // Add to cart hands back only the lines it added, which also carry an
    // `items` array. A real cart has an item_count, so that is the tell.
    if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number') {
      this.checkAndManageGifts(cart);
    } else {
      this.#reconcileFromServer();
    }
  };

  #reconcileFromServer() {
    fetch('/cart.js')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('cart fetch failed'))))
      .then((cart) => this.checkAndManageGifts(cart))
      .catch((error) => console.debug('GiftManager: cart fetch failed.', error));
  }

  /**
   * Brings the cart's gifts in line with the rules.
   *
   * @param {any} cartData a full Shopify cart object
   * @param {{announce?: boolean}} [options] announce dispatches cart:update for other components
   * @returns {Promise<any|null>} the updated cart, or null when nothing changed
   */
  checkAndManageGifts = async (cartData, options = {}) => {
    const { announce = true } = options;
    const config = this.#config;

    if (this.#isProcessing || !config || !cartData || !Array.isArray(cartData.items)) return null;

    this.#isProcessing = true;

    try {
      const updates = resolveGiftUpdates(cartData, config);
      if (Object.keys(updates).length === 0) return null;

      // Visible in DevTools when a gift appears or disappears unexpectedly:
      // the keys are variant ids (add) or line keys (change), values are the
      // quantities being set. Zero means the line is removed.
      console.debug('GiftManager: applying', updates, 'main units:', countMainUnits(cartData, config.mainProductId));

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: this.#sectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return null;

      const updatedCart = await response.json();

      if (announce) {
        document.documentElement.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              resource: updatedCart,
              sourceId: SOURCE_ID,
              data: { sections: updatedCart.sections, itemCount: updatedCart.item_count },
            },
          })
        );
      }

      return updatedCart;
    } catch (error) {
      console.debug('GiftManager: check failed.', error);
      return null;
    } finally {
      this.#isProcessing = false;
    }
  };

  /** Every cart section on the page, so one update re-renders all of them. */
  #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter((id) => Boolean(id));
  }
}

if (!customElements.get(SOURCE_ID)) {
  customElements.define(SOURCE_ID, GiftManagerComponent);
}
